import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import api from 'state/axios';
import { addDays, isWithinInterval } from "date-fns";
import toast from "react-hot-toast";
import { Formik } from "formik";
import * as yup from "yup";
import {
    useTheme,
    useMediaQuery,
    Box,
    TextField,
    Button,
    Typography,
    CircularProgress,
    MenuItem, 
    Select, 
    FormControl, 
    InputLabel,
} from "@mui/material";
import { ArrowBackIosNew } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { setFlights } from "state";
import { LAYOUT_HEIGHT } from "state/constants";
import { fetchFlights } from 'state/api';
import { parseUTCDate } from "state/helpers";
import FlexBetween from "components/FlexBetween";
import FlexCenter from "components/FlexCenter";
import Header from "components/Header";
import CustomDatePicker from "components/CustomDatePicker";
import StyledButton from 'components/StyledButton';


const Flight = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();   
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const { startDate, endDate } = useDateRangeContext();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [airports, setAirports] = useState([]);
    const [flight, setFlight] = useState({});
    
    
    const initialValues = {
        flightDate: flight.flightDate ? new Date(flight.flightDate) : null,
        flightActualDayDep: flight.flightActualDayDep ? new Date(flight.flightActualDayDep) : null,
        flightActualDayArr: flight.flightActualDayArr ? new Date(flight.flightActualDayArr) : null,
        acType: flight.acType ?? "",
        flightReg: flight.flightReg ?? "",
        flightType: flight.flightType ?? "",
        flightDesc: flight.flightDesc ?? "",
        flightCarrier: flight.flightCarrier ?? "",
        flightNo: flight.flightNo ?? 0,  
        dep: flight.dep ?? "",
        arr: flight.arr ?? "",
        std: flight.std ?? "",
        sta: flight.sta ?? "",
        etd: flight.etd ?? "",
        eta: flight.eta ?? "",
        atd: flight.atd ?? "",
        ata: flight.ata ?? "",
        noOfPax: flight.noOfPax ?? 0,
        remarks: flight.remarks ?? "",
    };
   
    const fetchAirports = useCallback(async () => {
        return await api.get('/airports');
    }, []);

    const fetchFlight = useCallback(async () => {        
        if (id) {
            return await api.get(`/flights/${id}`);       
        }
    }, [id]);
    
    useEffect(() => {
        const fetchData = async () => {
            try {
                setError(null);
                setLoading(true);
                
                const [
                    airportsResponse,
                    flightResponse
                ] = await Promise.all([
                    fetchAirports(),                       
                    id ? fetchFlight() : Promise.resolve(null),
                ]);
                
                setAirports(airportsResponse.data);                
                setFlight(flightResponse.data);                                        
            } catch (error) {
                setError(error);
                toast.error(error.response?.data?.message || "Failed to fetch data!");
            } finally {
                setLoading(false);
            }
        };
    
        fetchData();      
    }, [fetchAirports,     
        fetchFlight,        
        id
    ]); 
       
    const handleFlights = useCallback(async () => {
        if (!startDate || !endDate) return null; 
        
        try {
            setError(null);
            setLoading(true);
            
            const fetchedFlights = await fetchFlights(addDays(startDate, -1), addDays(endDate, 1));      
            if (fetchedFlights) {        
                const filteredFlights = fetchedFlights.filter(flight => {
                    const flightDateToCompare =
                    timeMode === 'UTC' ? flight.flightDate : flight.flightDateLocalBase;
                
                    return isWithinInterval(flightDateToCompare, { start: startDate, end: endDate });
                });
                
                dispatch(setFlights({ flights: filteredFlights }));
            }
        } catch (error) {
            setError(error);
            toast.error(error?.message || "Failed to load flights!");
        } finally {
            setLoading(false);
        }
    }, [dispatch, timeMode, startDate, endDate]);

    const validationSchema = yup.object().shape({
        flightDate: yup.date().required("Required"),
        flightNo: yup.number().required("Required"),
        dep: yup.string().required("Required"),
    });

    const handleSubmit = async (values, actions) => {
        try {
            const payload = {
                ...values,
                flightDate: values.flightDate ? parseUTCDate(values.flightDate) : null,
                flightActualDayDep: values.flightActualDayDep ? parseUTCDate(values.flightActualDayDep) : null,
                flightActualDayArr: values.flightActualDayArr ? parseUTCDate(values.flightActualDayArr) : null,
            };

            const method = id ? "put" : "post";
            const url = id
                ? `/flights/update/${id}`
                : '/flights/new';

            const res = await api({ method, url, data: payload });

            if (![200, 201].includes(res.status)) {
                toast.error("Failed to save flight!");
                return;
            }

            toast.success(id ? "Flight updated successfully." : "Flight created successfully.");
            handleFlights();
            navigate("/flightschedule");
        } catch (err) {
            toast.error(err.message || "Failed to submit flight!");
        } finally {
            actions.setSubmitting(false);
        }
    };


    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="Flight"
                    subtitle={loading ? "Loading..." : id ? "Edit Flight" : "Create Flight"}
                />
                {!loading && (
                    <StyledButton onClick={() => navigate("/flightschedule")}>
                        <ArrowBackIosNew />
                    </StyledButton>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">{error.message}</Typography>
                </FlexCenter>
            ) : (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Formik
                        enableReinitialize
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                            setFieldValue,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gap={2}
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    sx={{
                                        "& > div": { gridColumn: isDesktop ? undefined : "span 12" },
                                    }}
                                >
                                    <CustomDatePicker
                                        label="Flight Date"
                                        name="flightDate"
                                        value={values.flightDate}
                                        onChange={setFieldValue}
                                        touched={touched}
                                        errors={errors}
                                        sx={{ gridColumn: "span 3" }}
                                    />
                                    <CustomDatePicker
                                        label="Actual Departure Day"
                                        name="flightActualDayDep"
                                        value={values.flightActualDayDep}
                                        onChange={setFieldValue}
                                        touched={touched}
                                        errors={errors}
                                        sx={{ gridColumn: "span 3" }}
                                    />
                                    <CustomDatePicker
                                        label="Actual Arrival Day"
                                        name="flightActualDayArr"
                                        value={values.flightActualDayArr}
                                        onChange={setFieldValue}
                                        touched={touched}
                                        errors={errors}
                                        sx={{ gridColumn: "span 3" }}
                                    />
                                    <TextField
                                        type="number"
                                        label="Flight No"
                                        name="flightNo"
                                        variant="filled"
                                        onChange={handleChange}
                                        value={values.flightNo}
                                        error={!!touched.flightNo && !!errors.flightNo}
                                        helperText={touched.flightNo && errors.flightNo}
                                        sx={{ gridColumn: "span 3" }}
                                    />        
                                    <FormControl 
                                        required 
                                        variant="filled"
                                        sx={{ gridColumn: "span 3" }}
                                    >
                                        <InputLabel>Departure</InputLabel>
                                        <Select
                                            variant="filled"                                      
                                            label="Dep"
                                            name="dep"
                                            value={values.dep}
                                            onChange={handleChange}
                                        >
                                            {airports?.map(ap => (
                                                <MenuItem key={ap.iataCode} value={ap.iataCode}>
                                                    {ap.iataCode + ' - ' + ap.airportName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>                            
                                    <FormControl 
                                        required 
                                        variant="filled"
                                        sx={{ gridColumn: "span 3" }}
                                    >
                                        <InputLabel>Arrival</InputLabel>
                                        <Select
                                            variant="filled"                                      
                                            label="Arr"
                                            name="arr"
                                            value={values.arr}
                                            onChange={handleChange}
                                        >
                                            {airports?.map(ap => (
                                                <MenuItem key={ap.iataCode} value={ap.iataCode}>
                                                    {ap.iataCode + ' - ' + ap.airportName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl> 
                                    <TextField
                                        variant="filled"
                                        label="Aircraft Type"
                                        name="acType"
                                        value={values.acType}
                                        onChange={handleChange}
                                        sx={{ gridColumn: "span 3" }}
                                    />
                                    <TextField
                                        variant="filled"
                                        label="Registration"
                                        name="flightReg"
                                        value={values.flightReg || ''}
                                        onChange={handleChange}
                                        sx={{ gridColumn: "span 3" }}
                                    />
                                    <TextField
                                        variant="filled"
                                        label="STD"
                                        name="std"
                                        value={values.std}
                                        onChange={handleChange}
                                        sx={{ gridColumn: "span 3" }}
                                    />
                                    <TextField
                                        variant="filled"
                                        label="STA"
                                        name="sta"
                                        value={values.sta}
                                        onChange={handleChange}
                                        sx={{ gridColumn: "span 3" }}
                                    />
                                    <TextField
                                        variant="filled"
                                        label="ETD"
                                        name="etd"
                                        value={values.etd}
                                        onChange={handleChange}
                                        sx={{ gridColumn: "span 3" }}
                                    />
                                    <TextField
                                        variant="filled"
                                        label="ETA"
                                        name="eta"
                                        value={values.eta}
                                        onChange={handleChange}
                                        sx={{ gridColumn: "span 3" }}
                                    />
                                    <TextField
                                        variant="filled"
                                        label="ATD"
                                        name="atd"
                                        value={values.atd}
                                        onChange={handleChange}
                                        sx={{ gridColumn: "span 3" }}
                                    />
                                    <TextField
                                        variant="filled"
                                        label="ATA"
                                        name="ata"
                                        value={values.ata}
                                        onChange={handleChange}
                                        sx={{ gridColumn: "span 3" }}
                                    />
                                    <TextField
                                        variant="filled"
                                        type="number"
                                        label="No. of Pax"
                                        name="noOfPax"
                                        value={values.noOfPax}
                                        onChange={handleChange}
                                        sx={{ gridColumn: "span 3" }}
                                    />
                                    <TextField
                                        label="Remarks"
                                        name="remarks"
                                        variant="filled"
                                        onChange={handleChange}
                                        value={values.remarks}
                                        sx={{ gridColumn: "span 3" }}
                                    />
                                    
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}                                           
                                    >
                                        {id ? "Update" : "Create"}
                                    </Button> 
                                </Box>
                            </form>
                        )}
                    </Formik>
                </LocalizationProvider>
            )}
        </Box>
    );
};

export default Flight;
